import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, isToday, isThisWeek, isThisMonth, subDays } from 'date-fns';
import { mindfulnessService } from '../../../services/mindfulness.service';
import { useAuth } from '../../../context/AuthContext';
import './MindfulnessTimeline.css';

const MindfulnessTimeline = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedDay, setSelectedDay] = useState('today');
  const { user } = useAuth();

  useEffect(() => {
    let unsubscribe;

    if (user) {
      unsubscribe = mindfulnessService.subscribeSessions(user.uid, (loadedSessions) => {
        setSessions(loadedSessions);
      });
    }

    return () => unsubscribe && unsubscribe();
  }, [user]);

  const getFilteredSessions = () => {
    return sessions.filter(session => {
      if (!session.createdAt) return false;
      const sessionDate = session.createdAt.toDate();
      
      switch (selectedDay) {
        case 'today':
          return isToday(sessionDate);
        case 'week':
          return isThisWeek(sessionDate, { weekStartsOn: 1 });
        case 'month':
          return isThisMonth(sessionDate);
        default:
          return true;
      }
    }).sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toDate() - a.createdAt.toDate();
    });
  };

  const filteredSessions = getFilteredSessions();

  return (
    <div className="mindfulness-timeline">
      <div className="mindfulness-timeline-header">
        <h2>Mindfulness Sessions</h2>
        <div className="day-selector">
          <button 
            className={`day-option ${selectedDay === 'today' ? 'selected' : ''}`}
            onClick={() => setSelectedDay('today')}
          >
            Today
          </button>
          <button 
            className={`day-option ${selectedDay === 'week' ? 'selected' : ''}`}
            onClick={() => setSelectedDay('week')}
          >
            This Week
          </button>
          <button 
            className={`day-option ${selectedDay === 'month' ? 'selected' : ''}`}
            onClick={() => setSelectedDay('month')}
          >
            This Month
          </button>
        </div>
      </div>

      <div className={`mindfulness-sessions ${filteredSessions.length <= 1 ? 'single-session' : ''} ${filteredSessions.length === 0 ? 'no-sessions-height' : ''}`}>
        {filteredSessions.length === 0 ? (
          <motion.div 
            className="no-sessions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>No mindfulness sessions recorded</p>
            <small>Your mindfulness journey starts here</small>
          </motion.div>
        ) : (
          <motion.div 
            className="sessions-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {filteredSessions.map((session) => (
              <motion.div 
                key={session.id}
                className="session-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="session-date">
                  {format(session.createdAt.toDate(), 'MMM d, yyyy h:mm a')}
                </span>
                <p className="session-text">{session.text}</p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MindfulnessTimeline; 